import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import DateTimePickerInput from './DateTimePickerInput';
import { getDateOnlyFromISO } from '../utils/dateHelpers';
import { stripHtml } from '../utils/sanitize';
import {
  getTimeSpentValidationError,
  TIME_SPENT_INCREMENT_MINUTES,
} from '../utils/timeEntryTimeSpent';

export type TimeEntryInlineField =
  | 'workedDate'
  | 'customer'
  | 'engagement'
  | 'group'
  | 'type'
  | 'task'
  | 'hours'
  | 'description';

export interface TimeEntryInlineEditEntry {
  id: string;
  userId: string;
  customerId: string;
  engagementId: string;
  groupId: string;
  type: string;
  timeSpent: number;
  workedDate: string;
  description: string;
  task?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  Customer?: { id: string; accountName: string };
  Engagement?: { id: string; name: string; number?: string };
  Group?: { id: string; name: string };
}

interface Option {
  value: string;
  label: string;
}

interface TimeEntryInlineEditableCellProps {
  entry: TimeEntryInlineEditEntry;
  field: TimeEntryInlineField;
  canEditTimeEntries: boolean;
  isAdminOrManager: boolean;
  hasAdminRole: boolean;
  display: React.ReactNode;
  todayDate: string;
  onUpdate: (entryId: string, payload: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
  onValidationError?: (message: string) => void;
}

const FIELD_TITLES: Record<TimeEntryInlineField, string> = {
  workedDate: 'Double-click to edit worked date',
  customer: 'Double-click to edit customer',
  engagement: 'Double-click to edit engagement',
  group: 'Double-click to edit group',
  type: 'Double-click to edit type',
  task: 'Double-click to edit task',
  hours: 'Double-click to edit hours',
  description: 'Double-click to edit description',
};

const inputClass =
  'px-2 py-1 text-sm border-[1px] border-gray-200 rounded-md focus:ring-1 focus:ring-indigo-400 focus:border-indigo-300 bg-white';

function EditorChrome({
  children,
  onSave,
  onCancel,
  isSaving,
  className = '',
}: {
  children: React.ReactNode;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex items-start gap-1 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onSave}
        disabled={isSaving}
        className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50 flex-shrink-0 mt-0.5"
        title="Save"
      >
        <CheckIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCancel}
        disabled={isSaving}
        className="p-1 text-red-500 hover:text-red-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 flex-shrink-0 mt-0.5"
        title="Cancel"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
      {isSaving && (
        <span className="inline-block w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mt-1 flex-shrink-0" />
      )}
    </div>
  );
}

const TimeEntryInlineEditableCell: React.FC<TimeEntryInlineEditableCellProps> = ({
  entry,
  field,
  canEditTimeEntries,
  isAdminOrManager,
  hasAdminRole,
  display,
  todayDate,
  onUpdate,
  isSaving,
  onValidationError,
}) => {
  const canEditEntry = canEditTimeEntries && (entry.status !== 'APPROVED' || hasAdminRole);
  const canEditCustomerEngagement = isAdminOrManager && (entry.status !== 'APPROVED' || hasAdminRole);
  const canEditField =
    field === 'customer' || field === 'engagement'
      ? canEditEntry && canEditCustomerEngagement
      : canEditEntry;

  const [isEditing, setIsEditing] = useState(false);
  const [customerId, setCustomerId] = useState(entry.customerId);
  const [engagementId, setEngagementId] = useState(entry.engagementId);
  const [groupId, setGroupId] = useState(entry.groupId);
  const [type, setType] = useState(entry.type);
  const [task, setTask] = useState(entry.task || '');
  const [timeSpent, setTimeSpent] = useState(String(entry.timeSpent));
  const [workedDate, setWorkedDate] = useState(getDateOnlyFromISO(entry.workedDate));
  const [description, setDescription] = useState(stripHtml(entry.description || ''));
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const resetDraft = useCallback(() => {
    setCustomerId(entry.customerId);
    setEngagementId(entry.engagementId);
    setGroupId(entry.groupId);
    setType(entry.type);
    setTask(entry.task || '');
    setTimeSpent(String(entry.timeSpent));
    setWorkedDate(getDateOnlyFromISO(entry.workedDate));
    setDescription(stripHtml(entry.description || ''));
  }, [entry]);

  const handleStartEdit = () => {
    if (!canEditField) return;
    resetDraft();
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    resetDraft();
  };

  useEffect(() => {
    if (!isEditing) return;
    setTimeout(() => {
      if (field === 'description') {
        textareaRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, 0);
  }, [isEditing, field]);

  const customerQuery = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      const response = await api.get('/customers/dropdown');
      return response.data as { customers: { id: string; accountName: string }[] };
    },
    enabled: isEditing && field === 'customer',
  });

  const engagementCustomerId = field === 'customer' ? customerId : entry.customerId;
  const engagementQuery = useQuery({
    queryKey: ['engagements-dropdown', engagementCustomerId],
    queryFn: async () => {
      const response = await api.get(`/engagements/dropdown?customerId=${engagementCustomerId}`);
      return response.data as { engagements: { id: string; name: string }[] };
    },
    enabled: isEditing && (field === 'customer' || field === 'engagement') && !!engagementCustomerId,
  });

  // Groups/types depend on the engagement being edited (selected), not only the saved entry
  const optionsEngagementId =
    field === 'customer' || field === 'engagement' ? engagementId : entry.engagementId;
  const engagementChanged =
    (field === 'customer' || field === 'engagement') && engagementId !== entry.engagementId;

  const groupQuery = useQuery({
    queryKey: ['time-entry-user-groups', optionsEngagementId, entry.userId],
    queryFn: async () => {
      const params = new URLSearchParams({ engagementId: optionsEngagementId });
      if (isAdminOrManager && entry.userId) {
        params.append('userId', entry.userId);
      }
      const response = await api.get(`/time-entries/user-groups?${params.toString()}`);
      return response.data as { id: string; name: string }[];
    },
    enabled:
      isEditing &&
      !!optionsEngagementId &&
      (field === 'group' || field === 'customer' || field === 'engagement'),
  });

  const typeQuery = useQuery({
    queryKey: ['time-entry-types', optionsEngagementId],
    queryFn: async () => {
      const response = await api.get(`/time-entries/types?engagementId=${optionsEngagementId}`);
      return response.data as { value: string; label: string }[];
    },
    enabled:
      isEditing &&
      !!optionsEngagementId &&
      (field === 'type' || field === 'customer' || field === 'engagement'),
  });

  const customerOptions: Option[] = (customerQuery.data?.customers || []).map((c) => ({
    value: c.id,
    label: c.accountName,
  }));
  if (entry.customerId && !customerOptions.some((o) => o.value === entry.customerId)) {
    customerOptions.unshift({
      value: entry.customerId,
      label: entry.Customer?.accountName || 'Current customer',
    });
  }

  const engagementOptions: Option[] = (engagementQuery.data?.engagements || []).map((e) => ({
    value: e.id,
    label: e.name,
  }));
  if (entry.engagementId && !engagementOptions.some((o) => o.value === entry.engagementId)) {
    if (field === 'engagement' || customerId === entry.customerId) {
      engagementOptions.unshift({
        value: entry.engagementId,
        label: entry.Engagement?.name || 'Current engagement',
      });
    }
  }

  useEffect(() => {
    if (!isEditing || field !== 'customer') return;
    if (customerId === entry.customerId) return;
    const list = engagementQuery.data?.engagements;
    if (!list) return;
    if (engagementId && !list.some((e) => e.id === engagementId)) {
      setEngagementId('');
    }
  }, [isEditing, field, engagementQuery.data, customerId, engagementId, entry.customerId]);

  // When engagement changes, clear group/type (same behavior as edit page)
  useEffect(() => {
    if (!isEditing || (field !== 'customer' && field !== 'engagement')) return;
    if (!engagementChanged) return;
    setGroupId('');
    setType('');
  }, [isEditing, field, engagementId, engagementChanged]);

  // Drop type if it is no longer valid for the selected engagement's type list
  useEffect(() => {
    if (!isEditing) return;
    if (field !== 'customer' && field !== 'engagement' && field !== 'type') return;
    if (!optionsEngagementId || !typeQuery.data) return;
    if (!type) return;
    const allowed = typeQuery.data.some((t) => t.value === type);
    if (!allowed) {
      setType('');
    }
  }, [isEditing, field, optionsEngagementId, typeQuery.data, type]);

  // Drop group if it is no longer valid for the selected engagement
  useEffect(() => {
    if (!isEditing || (field !== 'customer' && field !== 'engagement')) return;
    if (!engagementId || !groupQuery.data) return;
    if (!groupId) return;
    const allowed = groupQuery.data.some((g) => g.id === groupId);
    if (!allowed) {
      setGroupId('');
    }
  }, [isEditing, field, engagementId, groupQuery.data, groupId]);

  const groupOptions: Option[] = (groupQuery.data || []).map((g) => ({
    value: g.id,
    label: g.name,
  }));
  if (
    entry.groupId &&
    !engagementChanged &&
    !groupOptions.some((o) => o.value === entry.groupId)
  ) {
    groupOptions.unshift({
      value: entry.groupId,
      label: entry.Group?.name || 'Current group',
    });
  }

  const typeOptions: Option[] = (typeQuery.data || []).map((t) => ({
    value: t.value,
    label: t.label,
  }));
  // Only keep the current type in the list when engagement did not change and types have not loaded yet
  if (
    entry.type &&
    !engagementChanged &&
    !typeQuery.data &&
    !typeOptions.some((o) => o.value === entry.type)
  ) {
    typeOptions.unshift({ value: entry.type, label: entry.type });
  }

  const handleSave = async () => {
    let payload: Record<string, unknown> | null = null;

    switch (field) {
      case 'workedDate': {
        if (!workedDate) {
          onValidationError?.('Worked Date is required');
          return;
        }
        if (todayDate && workedDate > todayDate) {
          onValidationError?.('Worked Date cannot be in the future');
          return;
        }
        payload = { workedDate };
        break;
      }
      case 'customer': {
        if (!customerId) {
          onValidationError?.('Customer is required');
          return;
        }
        if (!engagementId) {
          onValidationError?.('Engagement is required');
          return;
        }
        if (!type) {
          onValidationError?.('Type is required for the selected engagement');
          return;
        }
        if (engagementChanged && !groupId) {
          onValidationError?.('Group is required for the selected engagement');
          return;
        }
        payload = {
          customerId,
          engagementId,
          type,
          ...(groupId ? { groupId } : {}),
        };
        break;
      }
      case 'engagement': {
        if (!engagementId) {
          onValidationError?.('Engagement is required');
          return;
        }
        if (!type) {
          onValidationError?.('Type is required for the selected engagement');
          return;
        }
        if (engagementChanged && !groupId) {
          onValidationError?.('Group is required for the selected engagement');
          return;
        }
        payload = {
          engagementId,
          type,
          ...(groupId ? { groupId } : {}),
        };
        break;
      }
      case 'group': {
        if (!groupId) {
          onValidationError?.('Group is required');
          return;
        }
        payload = { groupId };
        break;
      }
      case 'type': {
        if (!type) {
          onValidationError?.('Type is required');
          return;
        }
        payload = { type };
        break;
      }
      case 'task':
        payload = { task: task.trim() };
        break;
      case 'hours': {
        const validationError = getTimeSpentValidationError(timeSpent);
        if (validationError) {
          onValidationError?.(validationError);
          return;
        }
        payload = { timeSpent: parseInt(timeSpent, 10) };
        break;
      }
      case 'description': {
        if (!description.trim()) {
          onValidationError?.('Description is required');
          return;
        }
        payload = { description: description.trim() };
        break;
      }
      default:
        return;
    }

    try {
      await onUpdate(entry.id, payload);
      setIsEditing(false);
    } catch {
      // Error toast handled by parent mutation
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && field !== 'description') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (field === 'workedDate' || field === 'customer' || field === 'engagement' || field === 'group' || field === 'type') {
      return;
    }
    if (e.relatedTarget && containerRef.current?.contains(e.relatedTarget as Node)) return;
    handleCancel();
  };

  if (isEditing && canEditField) {
    let editor: React.ReactNode = null;

    if (field === 'workedDate') {
      editor = (
        <div className="min-w-[180px]">
          <DateTimePickerInput
            value={workedDate}
            onChange={setWorkedDate}
            max={todayDate}
            showTimeSelect={false}
            placeholder="Select date"
          />
        </div>
      );
    } else if (field === 'customer') {
      editor = (
        <div className="flex flex-col gap-1 min-w-[200px]">
          <select
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              if (e.target.value !== entry.customerId) {
                setEngagementId('');
                setGroupId('');
                setType('');
              } else {
                setEngagementId(entry.engagementId);
                setGroupId(entry.groupId);
                setType(entry.type);
              }
            }}
            className={inputClass}
            disabled={isSaving || customerQuery.isLoading}
          >
            <option value="">Select Customer</option>
            {customerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={engagementId}
            onChange={(e) => {
              setEngagementId(e.target.value);
              // Always clear dependent fields; effect also clears when engagement differs
              if (e.target.value !== entry.engagementId) {
                setGroupId('');
                setType('');
              } else {
                setGroupId(entry.groupId);
                setType(entry.type);
              }
            }}
            className={inputClass}
            disabled={isSaving || !customerId || engagementQuery.isLoading}
          >
            <option value="">Select Engagement</option>
            {engagementOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {engagementId && (
            <>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={inputClass}
                disabled={isSaving || typeQuery.isLoading}
              >
                <option value="">Select Type</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className={inputClass}
                disabled={isSaving || groupQuery.isLoading}
              >
                <option value="">Select Group</option>
                {groupOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      );
    } else if (field === 'engagement') {
      editor = (
        <div className="flex flex-col gap-1 min-w-[200px]">
          <select
            value={engagementId}
            onChange={(e) => {
              setEngagementId(e.target.value);
              if (e.target.value !== entry.engagementId) {
                setGroupId('');
                setType('');
              } else {
                setGroupId(entry.groupId);
                setType(entry.type);
              }
            }}
            className={`${inputClass} min-w-[160px]`}
            disabled={isSaving || engagementQuery.isLoading}
          >
            <option value="">Select Engagement</option>
            {engagementOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {engagementId && (
            <>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={inputClass}
                disabled={isSaving || typeQuery.isLoading}
              >
                <option value="">Select Type</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className={inputClass}
                disabled={isSaving || groupQuery.isLoading}
              >
                <option value="">Select Group</option>
                {groupOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      );
    } else if (field === 'group') {
      editor = (
        <select
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          className={`${inputClass} min-w-[140px]`}
          disabled={isSaving || groupQuery.isLoading}
        >
          <option value="">Select Group</option>
          {groupOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    } else if (field === 'type') {
      editor = (
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className={`${inputClass} min-w-[160px]`}
          disabled={isSaving || typeQuery.isLoading}
        >
          <option value="">Select Type</option>
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    } else if (field === 'task') {
      editor = (
        <input
          ref={inputRef}
          type="text"
          value={task}
          onChange={(e) => setTask(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={`${inputClass} w-36`}
          disabled={isSaving}
          placeholder="Task"
        />
      );
    } else if (field === 'hours') {
      editor = (
        <input
          ref={inputRef}
          type="number"
          min={TIME_SPENT_INCREMENT_MINUTES}
          step={TIME_SPENT_INCREMENT_MINUTES}
          value={timeSpent}
          onChange={(e) => setTimeSpent(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          title="15-minute increments only (15, 30, 45, 60, ...)"
          className={`${inputClass} w-20`}
          disabled={isSaving}
        />
      );
    } else if (field === 'description') {
      editor = (
        <textarea
          ref={textareaRef}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          rows={2}
          className={`${inputClass} w-56`}
          disabled={isSaving}
        />
      );
    }

    return (
      <div ref={containerRef}>
        <EditorChrome onSave={handleSave} onCancel={handleCancel} isSaving={isSaving}>
          {editor}
        </EditorChrome>
      </div>
    );
  }

  return (
    <span
      className={canEditField ? 'cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1 block' : undefined}
      onDoubleClick={canEditField ? handleStartEdit : undefined}
      title={canEditField ? FIELD_TITLES[field] : undefined}
    >
      {display}
    </span>
  );
};

export default TimeEntryInlineEditableCell;
